import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethods } from '../../../shared/constants/application.constant';
import { apiUrlOnBoarding } from '../constants/apiUrl.onBoarding';
import {
    AddressResponse,
    BusinessParams,
    ChargeCCParams,
    ChargeCCRes,
    GooglePlacesResponse,
    LatLngRequest,
    PaymentPlan,
} from '../utils/slice.types';
import {
    ListResponse,
    SubCategory,
} from '../../../shared/utils/types.elements';
import { ConstNumber } from '../../../shared/constants/number.constant';

export const authApi = createApi({
    reducerPath: 'onBoarding',
    baseQuery: interceptorMiddleware,
    tagTypes: ['onBoarding'],
    endpoints: (builder) => ({
        logoutUser: builder.mutation<{}, { registration_id: string }>({
            query: ({ registration_id }) => {
                return {
                    url: apiUrlOnBoarding.LOGOUT,
                    method: HttpMethods.POST,
                    body: {
                        registration_id,
                    },
                };
            },
        }),
        getBusinessByPhone: builder.mutation<
            { data: GooglePlacesResponse },
            BusinessParams
        >({
            query: (body: BusinessParams) => {
                return {
                    url: apiUrlOnBoarding.GOOGLE_BUSINESS,
                    method: HttpMethods.POST,
                    body,
                };
            },
        }),
        getAddressByLatLng: builder.mutation<AddressResponse, LatLngRequest>({
            query: ({ lat, lng, place_id }: LatLngRequest) => {
                return {
                    url: apiUrlOnBoarding.GET_ADDRESS_FROM_LATLNG,
                    method: HttpMethods.POST,
                    body: {
                        latitude: lat,
                        longitude: lng,
                        place_id,
                    },
                };
            },
            transformResponse: (response: { data: AddressResponse }) =>
                response.data, // Extract "data" key from the response
        }),
        verifyTestOtp: builder.mutation<
            { message: string },
            { username: string }
        >({
            query: ({ username }) => {
                return {
                    url: apiUrlOnBoarding.VERIFY_TEST_USER,
                    method: HttpMethods.POST,
                    body: {
                        username,
                    },
                };
            },
        }),
        resetPassword: builder.mutation<
            { message: string },
            { username: string; password: string }
        >({
            query: ({ username, password }) => {
                return {
                    url: apiUrlOnBoarding.RESET_PASSWORD,
                    method: HttpMethods.POST,
                    body: {
                        username,
                        password,
                    },
                };
            },
        }),
        completeUser: builder.mutation({
            query: () => {
                return {
                    url: apiUrlOnBoarding.USER_REGISTER,
                    method: HttpMethods.POST,
                };
            },
        }),
        getL1CategoriesAssociatedWithPayment: builder.query<
            ListResponse<SubCategory>,
            {}
        >({
            query: () => ({
                url: `${apiUrlOnBoarding.GET_L1CATEGORIES}`,
                method: HttpMethods.GET,
            }),
        }),
        getPaymentPlan: builder.mutation<
            PaymentPlan,
            { id: number; promo_code: string }
        >({
            query: ({ id, promo_code }) => {
                const params = new URLSearchParams();
                params.append('promo_code', promo_code);
                return {
                    url: `${
                        apiUrlOnBoarding.GET_PAYMENT_PLAN
                    }/${id}/?${params.toString()}`,
                    method: HttpMethods.GET,
                };
            },
        }),
        getPaymentPlanOnMount: builder.query<
            PaymentPlan,
            { id: number; promo_code: string }
        >({
            query: ({ id, promo_code }) => {
                const params = new URLSearchParams();
                params.append('promo_code', promo_code);
                return {
                    url: `${
                        apiUrlOnBoarding.GET_PAYMENT_PLAN
                    }/${id}/?${params.toString()}`,
                    method: HttpMethods.GET,
                };
            },
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
        chargeCreditCard: builder.mutation<ChargeCCRes, ChargeCCParams>({
            query: (data) => {
                return {
                    url: apiUrlOnBoarding.CHARGE_CC,
                    method: HttpMethods.POST,
                    body: data,
                };
            },
        }),
        getSubscriptionInfoByCatgeoryId: builder.mutation<
            { data: any },
            { level1_id: number }
        >({
            query: ({ level1_id }) => {
                return {
                    url: apiUrlOnBoarding.GET_SUBSCRIPTION,
                    method: HttpMethods.POST,
                    body: {
                        level1_id,
                    },
                };
            },
        }),
        subscribeNewsLetter: builder.mutation<
            { message: string },
            { is_subscribe: boolean }
        >({
            query: ({ is_subscribe }) => {
                return {
                    url: apiUrlOnBoarding.NEWSLETTER_STATUS,
                    method: HttpMethods.POST,
                    body: {
                        newsletter_enabled: is_subscribe,
                    },
                };
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useLogoutUserMutation,
    useGetBusinessByPhoneMutation,
    useVerifyTestOtpMutation,
    useResetPasswordMutation,
    useGetAddressByLatLngMutation,
    useCompleteUserMutation,
    useGetL1CategoriesAssociatedWithPaymentQuery,
    useGetSubscriptionInfoByCatgeoryIdMutation,
    useGetPaymentPlanOnMountQuery,
    useGetPaymentPlanMutation,
    useSubscribeNewsLetterMutation,
    useChargeCreditCardMutation,
} = authApi;
